<script setup lang="ts">
import {
  DatetimePopup,
  Fragment,
  useClickOutside,
  useDisclosure,
  useInternalLinkClickHandler,
  UserAvatar,
  useTimeSinceString,
} from '#shared'
import { computed, toRef, useTemplateRef } from 'vue'
import { UserProfilePreview } from '#features/user-profile-preview'
import { useFullscreenChatHref, type Chat } from '#entities/chat'
import { useMessagePreview } from '#entities/message'

const { chat } = defineProps<{ chat: Chat }>()
const emit = defineEmits<{ 'link-click': [] }>()

const href = useFullscreenChatHref(toRef(() => chat.id))
const handleLinkNavigation = useInternalLinkClickHandler()
const onLinkClick = (event: Event) => {
  handleLinkNavigation(event)
  emit('link-click')
}

const shownMessage = computed(() => chat.lastPeerMessage ?? chat.lastMessage)

const shownMessagePreview = useMessagePreview(shownMessage)
const timeSinceShownMessage = useTimeSinceString(computed(() => shownMessage.value?.createdAt))

const avatarWrapEl = useTemplateRef('avatarWrapEl')
const userProfileDisclosure = useDisclosure()
const onUserClick = () => userProfileDisclosure.open()

const datetimeEl = useTemplateRef('datetimeEl')
const datetimePopupDisclosure = useDisclosure()
useClickOutside(
  computed(() => [datetimeEl.value]),
  () => datetimePopupDisclosure.close()
)
</script>

<template>
  <li v-if="chat.peer" :class="$style.root">
    <div :class="$style.content">
      <button :class="$style.avatarWrap" ref="avatarWrapEl" @click="onUserClick">
        <UserAvatar size="small" :user="chat.peer" />
      </button>

      <div :class="$style.textWrap">
        <button :class="[$style.name, { [$style.nameWithFreshMessages]: chat.unreadCount }]" @click="onUserClick">
          {{ chat.peer.displayName }}
        </button>

        <Fragment v-if="shownMessage">
          <a :class="$style.messagePreview" :href="href" title="View chat" @click="onLinkClick">
            <span v-if="shownMessage.sender.id !== chat.peer.id">You: </span> {{ shownMessagePreview }}
          </a>

          <div
            :class="$style.datetime"
            ref="datetimeEl"
            @mouseenter="datetimePopupDisclosure.open"
            @mouseleave="datetimePopupDisclosure.close"
            @click="datetimePopupDisclosure.open"
          >
            {{ timeSinceShownMessage }}
          </div>

          <DatetimePopup
            horizontalPostion="left-inside"
            verticalPosition="bottom-outside"
            :date="shownMessage.createdAt"
            :targetEl="datetimeEl"
            :isShown="datetimePopupDisclosure.isOpen"
          />
        </Fragment>
      </div>
    </div>

    <UserProfilePreview
      :isOpen="userProfileDisclosure.isOpen"
      :user="chat.peer"
      :targetEl="avatarWrapEl"
      :verticalOffset="10"
      @close="userProfileDisclosure.close"
    />
  </li>
</template>

<style lang="scss" module>
.content {
  display: flex;
  gap: 10px;
  padding: 10px 25px;
  border-top: 1px solid #5e5e5e;
  text-decoration: none;
  color: white;
}

.avatarWrap {
  display: flex;
  height: fit-content;
  flex-shrink: 0;
}

.textWrap {
  display: flex;
  flex-direction: column;
  font-size: 14px;
  width: 100%;
}

.name {
  font-weight: 700;
  color: #397ae9;
  position: relative;
  width: fit-content;
}

.nameWithFreshMessages {
  &::after {
    content: '';
    width: 8px;
    height: 8px;
    background-color: white;
    border-radius: 50%;
    position: absolute;
    top: 2px;
    right: -15px;
  }
}

.messagePreview {
  word-break: break-word;
  display: block;
  border-radius: 4px;
  padding: 5px;
  margin-left: -5px;
  margin-right: 5px;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background-color: #1a1920;
  }
}

.datetime {
  font-weight: 300;
  font-size: 14px;
  color: #939393;
  white-space: nowrap;
  width: fit-content;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
